<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePendingQuestions } from 'dashboard/composables/usePendingQuestions';
import QuestionCard from './components/QuestionCard.vue';
import ReviewModal from './components/ReviewModal.vue';

const { t } = useI18n();

const {
  pendingQuestions,
  bots,
  loading,
  stats,
  meta,
  totalPages,
  hasQuestions,
  fetchQuestions,
  fetchBots,
  updateQuestion,
  deleteQuestion,
  approveQuestion,
  rejectQuestion,
  changePage,
} = usePendingQuestions();

// UI State
const selectedBotId = ref(null);
const selectedStatus = ref('pending');
const showReviewModal = ref(false);
const reviewingQuestion = ref(null);

// Selected bot object
const selectedBot = computed(() => {
  if (!selectedBotId.value) return null;
  return bots.value.find(b => b.id === selectedBotId.value);
});

// Status tabs
const statusTabs = computed(() => [
  { key: 'pending', label: t('OMNI.PENDING_QUESTIONS.STATUS_PENDING'), count: stats.value.pending },
  { key: 'approved', label: t('OMNI.PENDING_QUESTIONS.STATUS_APPROVED'), count: stats.value.approved },
  { key: 'rejected', label: t('OMNI.PENDING_QUESTIONS.STATUS_REJECTED'), count: stats.value.rejected },
]);

// Watch for filter changes
watch([selectedBotId, selectedStatus], async () => {
  const params = { status: selectedStatus.value };
  if (selectedBotId.value) params.agent_bot_id = selectedBotId.value;
  await fetchQuestions(params);
});

onMounted(async () => {
  await fetchBots();
  await fetchQuestions({ status: selectedStatus.value });
});

const handleReview = question => {
  reviewingQuestion.value = question;
  showReviewModal.value = true;
};

const handleApprove = async (question, answer) => {
  await approveQuestion(question.id, answer);
  showReviewModal.value = false;
  reviewingQuestion.value = null;
};

const handleReject = async (question, reason) => {
  await rejectQuestion(question.id, reason);
  showReviewModal.value = false;
  reviewingQuestion.value = null;
};

const handleDelete = async id => {
  if (confirm(t('OMNI.PENDING_QUESTIONS.CONFIRM_DELETE'))) {
    await deleteQuestion(id);
  }
};

const handlePageChange = async page => {
  const params = { status: selectedStatus.value };
  if (selectedBotId.value) params.agent_bot_id = selectedBotId.value;
  await changePage(page, params);
};

const handleClearBotFilter = () => {
  selectedBotId.value = null;
};
</script>

<template>
  <div class="flex flex-col h-full w-full min-w-0">
    <!-- Header -->
    <div
      class="flex items-center justify-between p-4 border-b border-slate-75 dark:border-slate-800"
    >
      <div>
        <h1 class="text-xl font-semibold text-slate-900 dark:text-slate-25">
          {{ t('OMNI.PENDING_QUESTIONS.HEADER') }}
        </h1>
        <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
          {{ t('OMNI.PENDING_QUESTIONS.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <!-- Status Tabs -->
    <div class="px-4 pt-4 border-b border-slate-75 dark:border-slate-800">
      <div class="flex gap-1">
        <button
          v-for="tab in statusTabs"
          :key="tab.key"
          class="px-4 py-2 text-sm font-medium rounded-t-lg transition-colors"
          :class="
            selectedStatus === tab.key
              ? 'bg-white dark:bg-slate-900 text-woot-600 border-t border-l border-r border-slate-200 dark:border-slate-700 -mb-px'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
          "
          @click="selectedStatus = tab.key"
        >
          {{ tab.label }}
          <span
            class="ml-2 px-2 py-0.5 text-xs rounded-full"
            :class="
              selectedStatus === tab.key
                ? 'bg-woot-100 dark:bg-woot-900/30 text-woot-600'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
            "
          >
            {{ tab.count }}
          </span>
        </button>
      </div>
    </div>

    <!-- Filters Bar -->
    <div
      class="p-4 border-b border-slate-75 dark:border-slate-800 flex gap-4 items-center"
    >
      <!-- Bot Filter -->
      <div class="flex items-center gap-2">
        <label class="text-sm text-slate-600 dark:text-slate-400">
          {{ t('OMNI.PENDING_QUESTIONS.FILTER_BY_BOT') }}:
        </label>
        <select
          v-model="selectedBotId"
          class="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 min-w-[180px]"
        >
          <option :value="null">
            {{ t('OMNI.PENDING_QUESTIONS.ALL_BOTS') }}
          </option>
          <option v-for="bot in bots" :key="bot.id" :value="bot.id">
            {{ bot.name }}
          </option>
        </select>
        <button
          v-if="selectedBotId"
          class="p-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          @click="handleClearBotFilter"
        >
          <span class="i-lucide-x w-4 h-4" />
        </button>
      </div>

      <!-- Stats summary -->
      <div class="ml-auto text-sm text-slate-500 dark:text-slate-400">
        {{ t('OMNI.PENDING_QUESTIONS.TOTAL_QUESTIONS') }}: {{ stats.total }}
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 overflow-hidden p-4">
      <div
        class="bg-white dark:bg-slate-900 rounded-lg border border-slate-75 dark:border-slate-800 p-4 h-full flex flex-col overflow-hidden"
      >
        <!-- Selected bot indicator -->
        <div
          v-if="selectedBot"
          class="mb-4 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400"
        >
          <span class="i-lucide-filter w-4 h-4" />
          {{ t('OMNI.PENDING_QUESTIONS.FILTERING_BY') }}:
          <span
            class="font-medium text-woot-600 bg-woot-50 dark:bg-woot-900/20 px-2 py-0.5 rounded"
          >
            {{ selectedBot.name }}
          </span>
        </div>

        <div v-if="loading" class="flex-1 flex items-center justify-center">
          <span class="i-lucide-loader-2 w-8 h-8 animate-spin text-woot-500" />
        </div>

        <div
          v-else-if="!hasQuestions"
          class="flex-1 flex flex-col items-center justify-center text-center p-4"
        >
          <span
            class="i-lucide-inbox text-4xl text-slate-300 dark:text-slate-600 mb-2"
          />
          <p class="text-sm text-slate-600 dark:text-slate-400">
            {{ t('OMNI.PENDING_QUESTIONS.EMPTY_STATE') }}
          </p>
        </div>

        <div v-else class="flex-1 overflow-y-auto">
          <div class="space-y-3">
            <QuestionCard
              v-for="question in pendingQuestions"
              :key="question.id"
              :question="question"
              @review="handleReview"
              @delete="handleDelete"
            />
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="mt-4 flex justify-center gap-2">
          <button
            v-for="page in totalPages"
            :key="page"
            class="px-3 py-1.5 text-sm rounded transition-colors"
            :class="
              page === meta.page
                ? 'bg-woot-500 text-white'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
            "
            @click="handlePageChange(page)"
          >
            {{ page }}
          </button>
        </div>
      </div>
    </div>

    <!-- Review Modal -->
    <ReviewModal
      :is-open="showReviewModal"
      :question="reviewingQuestion"
      @approve="handleApprove"
      @reject="handleReject"
      @close="showReviewModal = false"
    />
  </div>
</template>

